import { ArrowRight, Shield, CheckCircle, TrendingUp, Award, Users, Star, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';
import OptimizedImage from './OptimizedImage';
import SEO from './SEO';
import { pageSEO, faqSchemas } from '../seoData';
import RelatedServices from './RelatedServices';

const NRIFinancialPlanning = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO {...pageSEO.nriFinancialPlanning} faqJsonLd={faqSchemas.nriFinancialPlanning} />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-purple-50 pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="mb-6">
                <span className="inline-block bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-semibold tracking-wide">
                  SPECIALIZED NRI ADVISORY SERVICES
                </span>
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                NRI Financial Planning
                <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Made Simple & Secure
                </span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Expert financial planning for NRIs across USA, UK, UAE, Canada, Australia & Singapore. 
                Manage your Indian investments seamlessly with SEBI-registered advisors who understand 
                both Indian markets and NRI regulations.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105 inline-flex items-center justify-center shadow-xl"
                >
                  Schedule NRI Consultation
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <Link
                  to="/services"
                  className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-8 py-4 rounded-xl font-bold text-lg transition-all inline-flex items-center justify-center"
                >
                  View NRI Services
                </Link>
              </div>
            </div>
            
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <OptimizedImage 
                  src="https://images.pexels.com/photos/3184431/pexels-photo-3184431.jpeg?w=800&q=85" 
                  alt="NRI financial planning services India - Expert investment advisory for Non-Resident Indians in USA, UK, UAE, Canada with FEMA compliance and repatriation support"
                  className="w-full h-[500px]"
                  priority={true}
                  objectFit="cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/10 to-purple-600/10"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                25+ Years
              </div>
              <div className="text-gray-600">Serving NRI Clients</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                50+ Countries
              </div>
              <div className="text-gray-600">NRI Clients Worldwide</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                ₹150 Cr+
              </div>
              <div className="text-gray-600">NRI AUM</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                SEBI
              </div>
              <div className="text-gray-600">Registered & Compliant</div>
            </div>
          </div>
        </div>
      </section>

      {/* NRI Challenges */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              NRI Investment Challenges We Solve
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Understanding the unique challenges NRIs face when investing in India
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Regulatory Compliance</h3>
              <p className="text-gray-600 leading-relaxed">
                Navigate FEMA regulations, NRE/NRO accounts, TDS, repatriation rules, and Form 15CA/CB 
                with expert guidance. We ensure full compliance with both Indian and international laws.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mb-6">
                <Globe className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Remote Management</h3>
              <p className="text-gray-600 leading-relaxed">
                Manage your Indian investments from anywhere in the world through digital platforms, 
                video consultations, and regular portfolio updates via email and WhatsApp.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6">
                <TrendingUp className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Currency & Tax Optimization</h3>
              <p className="text-gray-600 leading-relaxed">
                Optimize returns considering rupee fluctuations, double taxation treaties (DTAA), 
                and tax-efficient investment structures for maximum wealth preservation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* NRI Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Comprehensive NRI Investment Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              End-to-end financial planning tailored for Non-Resident Indians
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "NRI Equity Portfolio",
                description: "Direct equity investments in Indian markets through NRE/NRO demat accounts with full repatriation benefits",
                minInvestment: "$2,000"
              },
              {
                title: "NRI Mutual Funds",
                description: "Diversified mutual fund portfolios with KYC completion support and systematic investment plans",
                minInvestment: "$100/month"
              },
              {
                title: "NRI Portfolio Management",
                description: "Dedicated PMS for NRIs with customized strategies and relationship manager support",
                minInvestment: "$75,000"
              },
              {
                title: "Real Estate Advisory",
                description: "Indian property investments guidance including FEMA compliance and rental income management",
                minInvestment: "As per property"
              },
              {
                title: "NRI Bonds & Fixed Income",
                description: "RBI bonds, NRE/NRO fixed deposits, and corporate bonds with tax-efficient structures",
                minInvestment: "$5,000"
              },
              {
                title: "Retirement Planning India",
                description: "Plan your India return or retirement corpus building through systematic NRI investments",
                minInvestment: "Goal-based"
              }
            ].map((service, index) => (
              <div key={index} className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6 hover:shadow-xl transition-all">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-700 mb-4 leading-relaxed">{service.description}</p>
                <div className="text-sm font-semibold text-blue-600">Min Investment: {service.minInvestment}</div>
              </div>
            ))}
          </div>
          
          <div className="mt-12 text-center">
            <Link
              to="/services"
              className="inline-flex items-center bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105 shadow-xl"
            >
              Explore All NRI Services
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* NRI Process */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Simple Onboarding Process for NRIs
            </h2>
            <p className="text-xl text-gray-600">
              Start investing in India from anywhere in the world
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: "01",
                title: "Video Consultation",
                description: "Schedule a video call to discuss your goals, risk profile, and investment timeline"
              },
              {
                step: "02",
                title: "Documentation Support",
                description: "We guide you through NRI KYC, PAN card, and NRE/NRO account setup"
              },
              {
                step: "03",
                title: "Portfolio Creation",
                description: "Customized investment strategy considering currency, tax, and repatriation needs"
              },
              {
                step: "04",
                title: "Ongoing Support",
                description: "Regular updates, rebalancing, and 24/7 support across time zones"
              }
            ].map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <span className="text-2xl font-bold text-white">{step.step}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              What NRI Clients Say
            </h2>
            <p className="text-xl text-gray-600">
              Success stories from NRIs across the globe
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: "Amit Patel",
                location: "New York, USA",
                testimonial: "Managing my Indian investments from the US was always confusing until I found Rumtek. They handle everything - compliance, taxes, repatriation. My NRI portfolio has grown 18% annually!"
              },
              {
                name: "Sneha Reddy",
                location: "Dubai, UAE",
                testimonial: "Best NRI financial planning service! They helped me set up everything remotely. Monthly updates and video consultations make managing my India investments so easy from Dubai."
              },
              {
                name: "Vijay Kumar",
                location: "London, UK",
                testimonial: "Their expertise in DTAA and NRI tax laws saved me thousands in taxes. Professional PMS service with full transparency. Highly recommend for UK-based NRIs."
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8 shadow-lg">
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic leading-relaxed">"{testimonial.testimonial}"</p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold mr-4">
                    {testimonial.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.location}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              NRI Investment FAQs
            </h2>
            <p className="text-xl text-gray-600">
              Common questions from our NRI clients
            </p>
          </div>
          
          <div className="space-y-6">
            {[
              {
                question: "Can NRIs invest in Indian stock market?",
                answer: "Yes! NRIs can invest in Indian equities through NRE/NRO demat accounts. You need a PAN card, NRI KYC, and overseas address proof. Investments via NRE accounts are fully repatriable. We handle the complete setup process."
              },
              {
                question: "What is the difference between NRE and NRO accounts?",
                answer: "NRE accounts hold foreign earnings (fully repatriable, tax-free interest). NRO accounts hold Indian income (repatriation limit $1M/year, interest taxable). We help you choose the right account structure for your investments."
              },
              {
                question: "Do NRIs need to pay tax on Indian investments?",
                answer: "NRIs pay capital gains tax in India. However, DTAA prevents double taxation. Long-term equity gains over ₹1L taxed at 10%, short-term at 15%. We optimize your portfolio for tax efficiency."
              },
              {
                question: "Can I manage investments remotely from abroad?",
                answer: "Absolutely! Our digital platform, video consultations, and WhatsApp support ensure seamless management from anywhere. You'll receive regular updates and can execute trades/transactions online 24/7."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <RelatedServices links={['nriUK', 'nriUAE', 'nriUS', 'mutualFund', 'pms', 'gold']} />

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-purple-900 to-blue-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Invest in India?
          </h2>
          <p className="text-xl text-blue-100 mb-10 leading-relaxed">
            Join hundreds of NRIs who trust Rumtek for their Indian investments. 
            Schedule your free consultation today and discover hassle-free NRI financial planning.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-white text-blue-900 px-10 py-5 rounded-xl font-bold text-lg hover:bg-blue-50 transition-all transform hover:scale-105 inline-flex items-center justify-center shadow-2xl"
            >
              Schedule NRI Consultation
              <ArrowRight className="ml-2 h-6 w-6" />
            </Link>
            <Link
              to="/calculator"
              className="border-2 border-white text-white hover:bg-white hover:text-blue-900 px-10 py-5 rounded-xl font-bold text-lg transition-all inline-flex items-center justify-center"
            >
              Calculate Returns
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NRIFinancialPlanning;